import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import BroadcastDialog from "@/components/BroadcastDialog";
import NewDirectThreadDialog from "@/components/NewDirectThreadDialog";
import { Send, MessageSquare, ArrowRight, Search, Archive, ArchiveRestore, Filter, PenSquare } from "lucide-react";
import { toast } from "sonner";
import { useAuth, isStudent } from "@/lib/auth";
import { fmtDateTime } from "@/lib/format";

export default function Threads() {
  const { user } = useAuth();
  const student = isStudent(user);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [bcOpen, setBcOpen] = useState(false);
  const [ndOpen, setNdOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [kind, setKind] = useState("all");
  const [showArchived, setShowArchived] = useState(false);
  const [unreadOnly, setUnreadOnly] = useState(false);

  const { data: threads = [], isLoading } = useQuery({
    queryKey: ["threads", { search, kind, showArchived, unreadOnly }],
    queryFn: async () => {
      const params = {};
      if (search) params.search = search;
      if (kind !== "all") params.kind = kind;
      if (showArchived) params.archived = true;
      if (unreadOnly) params.unread_only = true;
      return (await api.get("/threads", { params })).data;
    },
    refetchInterval: 20_000,
  });

  const archiveMut = useMutation({
    mutationFn: async ({ id, archive }) => (
      await api.patch(`/threads/${id}/${archive ? "archive" : "unarchive"}`)
    ).data,
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["threads"] });
      qc.invalidateQueries({ queryKey: ["threads-stats-badge"] });
      toast.success(vars.archive ? "Conversazione archiviata" : "Ripristinata");
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });

  const basePath = student ? "/portale/messaggi" : "/messaggi";
  const activeFilterCount = useMemo(() => (
    (search ? 1 : 0) + (kind !== "all" ? 1 : 0) + (unreadOnly ? 1 : 0) + (showArchived ? 1 : 0)
  ), [search, kind, unreadOnly, showArchived]);

  return (
    <div className="p-4 sm:p-6 md:p-10 max-w-[1200px] mx-auto">
      <PageHeader
        title={student ? "Messaggi" : "Comunicazioni interne"}
        subtitle={student
          ? "Conversazioni con la segreteria e comunicazioni ricevute."
          : "Conversazioni con gli studenti e broadcast alle classi."}
        actions={!student && (
          <div className="flex gap-2">
            <Button
              onClick={() => setNdOpen(true)}
              data-testid="direct-open-btn"
              variant="outline"
              className="rounded-full"
            >
              <PenSquare size={14} className="mr-1.5"/> Nuovo messaggio
            </Button>
            <Button
              onClick={() => setBcOpen(true)}
              data-testid="broadcast-open-btn"
              className="rounded-full bg-[#E2725B] hover:bg-[#c85d47]"
            >
              <Send size={14} className="mr-1.5"/> Nuovo broadcast
            </Button>
          </div>
        )}
      />

      <Card className="border border-stone-200 rounded-xl shadow-none">
        <div className="p-4 flex flex-col md:flex-row gap-3 border-b border-stone-100">
          <div className="relative flex-1 max-w-md">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400"/>
            <Input
              data-testid="thread-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Cerca oggetto conversazione…"
              className="pl-9"
            />
          </div>
          <Select value={kind} onValueChange={setKind}>
            <SelectTrigger className="w-44" data-testid="thread-kind-filter"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i tipi</SelectItem>
              <SelectItem value="direct">Diretti</SelectItem>
              <SelectItem value="broadcast">Broadcast</SelectItem>
              <SelectItem value="request">Richieste</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant={unreadOnly ? "default" : "outline"}
            data-testid="thread-unread-toggle"
            onClick={() => setUnreadOnly((v) => !v)}
            className={`rounded-full ${unreadOnly ? "bg-[#E2725B] hover:bg-[#c85d47] text-white" : ""}`}
          >
            <Filter size={13} className="mr-1.5"/> Non letti
          </Button>
          <Button
            variant={showArchived ? "default" : "outline"}
            data-testid="thread-archived-toggle"
            onClick={() => setShowArchived((v) => !v)}
            className={`rounded-full ${showArchived ? "bg-stone-700 hover:bg-stone-800 text-white" : ""}`}
          >
            <Archive size={13} className="mr-1.5"/> {showArchived ? "Archiviate" : "Attive"}
          </Button>
        </div>

        {isLoading ? (
          <div className="py-10 text-center text-stone-400 text-sm">Caricamento…</div>
        ) : threads.length === 0 ? (
          <div className="py-16 text-center text-stone-400 text-sm">
            <MessageSquare size={30} className="mx-auto text-stone-300 mb-3"/>
            {activeFilterCount > 0
              ? "Nessuna conversazione corrisponde ai filtri."
              : showArchived ? "Nessuna conversazione archiviata." : "Nessuna conversazione ancora."}
          </div>
        ) : (
          <ul className="divide-y divide-stone-100">
            {threads.map((t, i) => (
              <li
                key={t.id}
                data-testid={`thread-row-${t.id}`}
                className="p-4 md:p-5 flex items-start gap-4 hover:bg-stone-50 transition-colors animate-in-row group"
                style={{ animationDelay: `${i * 15}ms` }}
              >
                <div
                  onClick={() => navigate(`${basePath}/${t.id}`)}
                  className={`w-10 h-10 rounded-full grid place-items-center shrink-0 cursor-pointer ${t.unread > 0 ? "bg-[#E2725B] text-white" : t.archived ? "bg-stone-200 text-stone-500" : "bg-stone-100 text-stone-500"}`}
                >
                  {t.archived ? <Archive size={16}/> : <MessageSquare size={16}/>}
                </div>
                <div
                  onClick={() => navigate(`${basePath}/${t.id}`)}
                  className="min-w-0 flex-1 cursor-pointer"
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className={`${t.unread > 0 ? "font-semibold" : "font-medium"} text-[#1C352D]`}>{t.subject || "(senza oggetto)"}</div>
                    {t.kind === "broadcast" && <Badge className="bg-[#E2725B] text-white text-[9px] rounded-full">BROADCAST</Badge>}
                    {t.kind === "request" && <Badge className="bg-stone-100 text-stone-700 text-[9px] rounded-full">RICHIESTA</Badge>}
                    {t.archived && <Badge className="bg-stone-200 text-stone-700 text-[9px] rounded-full">ARCHIVIATA</Badge>}
                    {t.unread > 0 && <Badge className="bg-[#E2725B] text-white text-[9px] rounded-full">{t.unread} non letti</Badge>}
                  </div>
                  {!student && t.student_name && (
                    <div className="text-[11px] text-stone-500 mt-0.5">👤 {t.student_name} · {t.student_matricola}</div>
                  )}
                  {t.last_message_preview && (
                    <div className="text-sm text-stone-600 mt-1 truncate max-w-[700px]">{t.last_message_preview}</div>
                  )}
                  <div className="text-[10px] text-stone-400 mt-1">{fmtDateTime(t.last_message_at)}</div>
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); archiveMut.mutate({ id: t.id, archive: !t.archived }); }}
                  data-testid={`thread-archive-${t.id}`}
                  title={t.archived ? "Ripristina" : "Archivia"}
                  className="opacity-0 group-hover:opacity-100 transition-opacity p-2 rounded hover:bg-stone-100 text-stone-500 hover:text-[#1C352D] self-center"
                >
                  {t.archived ? <ArchiveRestore size={15}/> : <Archive size={15}/>}
                </button>
                <ArrowRight size={14} className="text-stone-400 self-center"/>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <BroadcastDialog open={bcOpen} onOpenChange={setBcOpen}/>
      <NewDirectThreadDialog
        open={ndOpen}
        onOpenChange={setNdOpen}
        onCreated={(t) => navigate(`${basePath}/${t.id}`)}
      />
    </div>
  );
}
